import { SupabaseClient } from '@supabase/supabase-js';
import { invalidateUserData } from '../src/lib/queryClient';
import { 
  CategoryInfo, 
  StoreInfo, 
  ShoppingHistoryEntry, 
  ShoppingItem, 
  InsightData, 
  UserProfile, 
  AiProfile,
  UserProfileSchema,
  mapFlatToDeep,
  BasketTip,
  ArchitectApplication,
  MerchantPartnerApplication
} from '../src/lib/schemas';
import { LatticeSeedItem } from '../src/types/latticeSeed';
import { CALIBRATION_DEFAULTS } from '../src/lib/calibration';
import type { RestockSignal } from '../src/lib/restock';

// #region Admin Enrollment API
export const getAllArchitectApplications = async (supabase: SupabaseClient) => {
  const { data, error } = await supabase.rpc('get_all_architect_applications');

  if (error) {
    console.error('Error fetching all architect applications via RPC:', error);
    throw error;
  }
  return data;
};

export const getAllPartnerApplications = async (supabase: SupabaseClient) => {
  const { data, error } = await supabase.rpc('get_all_partner_applications');

  if (error) {
    console.error('Error fetching all partner applications via RPC:', error);
    throw error;
  }
  return data;
};

export const updateArchitectApplicationStatus = async (
  supabase: SupabaseClient, 
  id: string, 
  status: 'approved' | 'rejected' | 'reviewing',
  notes?: string
) => {
  const { data, error } = await supabase
    .from('architect_applications')
    .update({ 
      status, 
      reviewer_notes: notes,
      reviewed_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating architect application status:', error);
    throw error;
  }
  return data;
};

export const updatePartnerApplicationStatus = async (
  supabase: SupabaseClient, 
  id: string, 
  status: 'approved' | 'rejected' | 'reviewing',
  notes?: string
) => {
  const { data, error } = await supabase
    .from('merchant_partner_applications')
    .update({ 
      status, 
      reviewer_notes: notes,
      reviewed_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating partner application status:', error);
    throw error;
  }
  return data;
};

export const promoteUserRole = async (
  supabase: SupabaseClient, 
  userId: string, 
  role: 'architect' | 'store_partner' | 'consumer' | 'admin'
) => {
  const { data, error } = await supabase
    .rpc('promote_user_role', {
      p_user_id: userId,
      p_role: role
    });

  if (error) {
    console.error('Error promoting user role:', error);
    throw error;
  }
  return data;
};

export const authorizeArchitect = async (supabase: SupabaseClient, applicationId: string, userId: string, notes?: string) => {
  const { data, error } = await supabase.rpc('authorize_architect', {
    p_application_id: applicationId,
    p_user_id: userId,
    p_notes: notes
  });
  if (error) {
    console.error('Error authorizing architect:', error);
    throw error;
  }
  return data;
};

export const authorizePartner = async (supabase: SupabaseClient, applicationId: string, userId: string, notes?: string) => {
  const { data, error } = await supabase.rpc('authorize_partner', {
    p_application_id: applicationId,
    p_user_id: userId,
    p_notes: notes
  });
  if (error) {
    console.error('Error authorizing partner:', error);
    throw error;
  }
  return data;
};

export const revokeArchitect = async (supabase: SupabaseClient, applicationId: string, userId: string, notes?: string) => {
  const { data, error } = await supabase.rpc('revoke_architect', {
    p_application_id: applicationId,
    p_user_id: userId,
    p_notes: notes
  });
  if (error) {
    console.error('Error revoking architect:', error);
    throw error;
  }
  return data;
};

export const revokePartner = async (supabase: SupabaseClient, applicationId: string, userId: string, notes?: string) => {
  const { data, error } = await supabase.rpc('revoke_partner', {
    p_application_id: applicationId,
    p_user_id: userId,
    p_notes: notes
  });
  if (error) {
    console.error('Error revoking partner:', error);
    throw error;
  }
  return data;
};
// #endregion

// #region Enrollment API
export const submitArchitectApplication = async (supabase: SupabaseClient, application: Omit<ArchitectApplication, 'id' | 'created_at' | 'status'>) => {
  const { data, error } = await supabase.rpc('submit_architect_application', {
    p_user_id: application.user_id,
    p_full_name: application.full_name,
    p_mobile: application.mobile,
    p_zip_code: application.zip_code,
    p_radius: application.radius,
    p_device_model: application.device_model,
    p_frequency: application.frequency,
    p_retailers: application.retailers,
    p_motivation: application.motivation
  });

  if (error) {
    console.error('Error submitting architect application via RPC:', error);
    throw error;
  }
  return data;
};

export const submitPartnerApplication = async (
  supabase: SupabaseClient, 
  application: {
    user_id: string;
    business_name: string;
    tax_id: string;
    category: string;
    operational_scale: number;
    address: string;
    foot_traffic: string;
    data_strategy: string;
    contact_first: string;
    contact_last: string;
    contact_email: string;
    contact_phone: string;
    goal: string;
    latitude?: number;
    longitude?: number;
    store_url?: string;
  }
) => {
  const { data, error } = await supabase.rpc('submit_partner_application', {
    p_user_id: application.user_id,
    p_business_name: application.business_name,
    p_tax_id: application.tax_id,
    p_category: application.category,
    p_operational_scale: application.operational_scale,
    p_address: application.address,
    p_foot_traffic: application.foot_traffic,
    p_data_strategy: application.data_strategy,
    p_contact_first: application.contact_first,
    p_contact_last: application.contact_last,
    p_contact_email: application.contact_email,
    p_contact_phone: application.contact_phone,
    p_goal: application.goal,
    p_lat: application.latitude,
    p_lng: application.longitude,
    p_store_url: application.store_url
  });

  if (error) {
    console.error('Error submitting partner application via RPC:', error);
    throw error;
  }
  return data;
};

export const getArchitectApplication = async (supabase: SupabaseClient, userId: string) => {
  const { data, error } = await supabase.rpc('get_architect_application', {
    p_user_id: userId
  });

  if (error) {
    console.error('Error fetching architect application via RPC:', error);
    throw error;
  }
  return data;
};

export const getPartnerApplication = async (supabase: SupabaseClient, userId: string) => {
  const { data, error } = await supabase.rpc('get_partner_application', {
    p_user_id: userId
  });

  if (error) {
    console.error('Error fetching partner application via RPC:', error);
    throw error;
  }
  return data;
};
// #endregion

// #region Profile API
export const getProfile = async (supabase: SupabaseClient, userId: string): Promise<UserProfile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, email, first_name, last_name, avatar_url, role, is_architect, is_partner, store_id, zip_code, ai_profile, settings, last_history_change, is_pro, is_ultra, subscription_interval, credits, has_onboarded, last_insight_at, storage_used_bytes, extra_storage_bytes, metadata, loyalty_programs')
    .eq('id', userId)
    .maybeSingle();
    
  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching profile:', error);
    throw error;
  }
  
  if (data) {
    // Standardize settings if they are missing/incomplete in DB
    const standardizedData = {
      ...data,
      settings: data.settings || {
        interface: { language: 'en', currency: 'USD' },
      }
    };

    const result = UserProfileSchema.safeParse(standardizedData);
    if (!result.success) {
      console.warn('[SupabaseService] Profile validation issues (using raw data fallback):', 
        result.error.issues.map(i => `${i.path.join('.')}: ${i.message}`).join(', ')
      );
      return standardizedData as UserProfile;
    }
    return result.data;
  }
  
  return data;
};

export const createProfile = async (supabase: SupabaseClient, userId: string, email: string, firstName?: string | null, lastName?: string | null) => {
  const { data, error } = await supabase
    .from('profiles')
    .insert([{ 
      id: userId, 
      email,
      first_name: firstName,
      last_name: lastName
    }])
    .select()
    .single();
    
  // A billing webhook or another tab may have provisioned this identity first.
  if (error?.code === '23505') {
    const existing = await getProfile(supabase, userId);
    if (existing) return existing;
  }
  if (error) {
    console.error('Error creating profile:', error);
    throw error;
  }
  
  // Ensure user folder exists in storage (fire-and-forget to avoid blocking)
  ensureUserFolder(supabase, userId).catch(err => console.error("Error ensuring user folder in background:", err));
  
  return data;
};

export const updateAiProfile = async (supabase: SupabaseClient, userId: string, aiProfile: AiProfile) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ ai_profile: aiProfile })
    .eq('id', userId)
    .select()
    .maybeSingle();
  
  if (error) {
    console.error('Error updating AI profile:', error);
    throw error;
  }
  
  return data;
};

/**
 * RLS hides rows the caller can't write, so a refused UPDATE answers 204 with
 * no error. Ask for the rows back (`.select('id')`) and pass the result here:
 * zero rows throws instead of passing for a save.
 */
export const requireWrittenRows = <T>({ data, error }: { data: T[] | null; error: unknown }, what: string): T[] => {
  if (error) throw error;
  if (!data || data.length === 0) throw new Error(`${what}: no row was changed`);
  return data;
};

export const updateProfile = async (supabase: SupabaseClient, userId: string, updates: Partial<UserProfile>) => {
  // CRITICAL: Metadata is a JSONB blob. To prevent overwriting, we should fetch current if updating
  // But since mapFlatToDeep handles the nesting, we just need to ensure the top-level updates 
  // passed to RPC are correctly structured.

  // Use the standardization protocol to map flat updates to deep-nested paths (e.g. language -> settings.interface.language)
  const deepUpdates = mapFlatToDeep(updates, UserProfileSchema);

  // ATOMIC SYNC: Use the patch_profile RPC to perform a server-side deep merge
  const { data, error } = await supabase.rpc('patch_profile', {
    p_user_id: userId,
    p_updates: deepUpdates
  });

  if (error) {
    console.error('SupabaseService: Error patching profile via RPC:', error);
    throw error;
  }
  // patch_profile returns null when it found no profile row to update.
  if (data == null) throw new Error('Profile not saved: no profile row was found');

  return data;
};
export const createStripeCheckoutSession = async (token: string, priceId: string) => {
  const response = await fetch('/api/billing/create-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ priceId })
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error("[Billing] Checkout Session Error Details:", errorData);
    throw new Error(errorData.details || errorData.error || 'Failed to create checkout session');
  }

  return await response.json();
};

export const getStripeProductDetails = async (priceId: string) => {
  const response = await fetch(`/api/billing/product-details/${priceId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch product details');
  }
  return await response.json();
};
// #endregion

// #region Storage API
export const ensureUserFolder = async (supabase: SupabaseClient, userId: string) => {
  try {
    // Check if any files exist in the user's folder
    const { data, error } = await supabase
      .storage
      .from('items')
      .list(userId, { limit: 1 });

    if (error) {
      console.error('Error listing items in user folder:', error);
      throw error;
    }

    // If no files exist, "create" the folder by uploading a .keep file
    if (!data || data.length === 0) {
      const { error: uploadError } = await supabase
        .storage
        .from('items')
        .upload(`${userId}/.keep`, new Blob([''], { type: 'text/plain' }), {
          upsert: true
        });
      if (uploadError) {
        console.error('Error uploading .keep file:', uploadError);
        throw uploadError;
      }
    }
  } catch (error) {
    console.error('Error ensuring user folder:', error);
    // Don't throw here to avoid blocking profile creation/app flow
  }
};

export const uploadItemImage = async (supabase: SupabaseClient, userId: string, file: Blob | File, fileName: string) => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 7);
  // Sanitize fileName to avoid 'Invalid key' errors from Supabase storage (spaces, special chars)
  // Stricter sanitization: replace all special characters with underscores
  const sanitizedFileName = fileName
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');
    
  const filePath = `${userId}/${timestamp}_${randomSuffix}_${sanitizedFileName}`;

  const { data, error } = await supabase
    .storage
    .from('items')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true
    });

  if (error) {
    console.error('Error uploading image:', error);
    throw error;
  }

  // ATOMIC SYNC: Increment the storage balance in the profile
  const fileSize = file instanceof Blob ? file.size : (file as File).size;
  await supabase.rpc('increment_storage', { p_user_id: userId, p_amount: fileSize });

  const { data: { publicUrl } } = supabase
    .storage
    .from('items')
    .getPublicUrl(filePath);

  return publicUrl;
};

/**
 * Same upload as uploadItemImage, additionally returning the Storage path
 * (not just the public URL) — needed by Bulk Import background processing,
 * which stores image_path in bulk_import_receipts for the cron task to
 * download directly via supabase.storage.from('items').download(path).
 * A new function rather than changing uploadItemImage's return shape, to
 * avoid touching its existing call sites.
 */
export const uploadItemImageWithPath = async (supabase: SupabaseClient, userId: string, file: Blob | File, fileName: string): Promise<{ publicUrl: string; path: string }> => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 7);
  const sanitizedFileName = fileName
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');

  const filePath = `${userId}/${timestamp}_${randomSuffix}_${sanitizedFileName}`;

  const { error } = await supabase
    .storage
    .from('items')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true
    });

  if (error) {
    console.error('Error uploading image:', error);
    throw error;
  }

  const fileSize = file instanceof Blob ? file.size : (file as File).size;
  await supabase.rpc('increment_storage', { p_user_id: userId, p_amount: fileSize });

  const { data: { publicUrl } } = supabase
    .storage
    .from('items')
    .getPublicUrl(filePath);

  return { publicUrl, path: filePath };
};

export const syncStorage = async (supabase: SupabaseClient, userId: string) => {
  try {
    // Server-side recount (item images + CartAssistant chat history). A direct
    // profiles update would be reverted by the privileged-column guard trigger.
    const { data, error } = await supabase.rpc('recalculate_storage', { p_user_id: userId });
    if (error) throw error;
    return Number(data ?? 0);
  } catch (e) {
    console.error("Failed to sync storage:", e);
    return null;
  }
};

export const deleteItemImage = async (supabase: SupabaseClient, imageUrl: string, userId?: string) => {
  if (!imageUrl || !imageUrl.includes('/items/')) {
    return; // Not a valid storage URL for this bucket
  }

  // If userId is provided, check if image is still in use in history
  if (userId) {
    try {
        const [{ data: h, error: historyError }, { data: i, error: itemsError }] = await Promise.all([
          supabase.from('shopping_history').select('id').eq('receipt_image_url', imageUrl).eq('user_id', userId).limit(1),
          supabase.from('shopping_items').select('id').eq('image_url', imageUrl).eq('user_id', userId).limit(1),
        ]);
        if (historyError || itemsError) return; // Uncertain usage must never delete a referenced photo.
        if ((h && h.length > 0) || (i && i.length > 0)) {
            return; // Skip deletion - image is in history
        }
    } catch (e) {
        console.error('Error checking image usage:', e);
        return;
    }
  }

  try {
    const parts = imageUrl.split('/items/');
    const imagePath = decodeURIComponent(parts[parts.length - 1]);
    if (userId && !imagePath.startsWith(`${userId}/`)) return;
    if (imagePath) {
      // Get file size for decrementing before deleting
      let fileSize = 0;
      if (userId) {
        const fileName = imagePath.split('/').pop();
        if (fileName) {
          const { data: files } = await supabase.storage.from('items').list(userId, { search: fileName });
          const file = files?.find(f => f.name === fileName);
          // @ts-ignore
          fileSize = file?.metadata?.size || file?.size || 0;
        }
      }

      const { error } = await supabase.storage.from('items').remove([imagePath]);
      if (!error && userId && fileSize > 0) {
        await supabase.rpc('decrement_storage', { p_user_id: userId, p_amount: fileSize });
      } else if (error) {
        // We don't throw here to avoid breaking UI flow if a single deletion fails
        console.error('Error deleting item image from storage:', error);
      }
    }
  } catch (e) {
    console.error('Could not delete item image:', e);
  }
};

export const deleteItemImages = async (supabase: SupabaseClient, imageUrls: string[], userId?: string) => {
  if (!imageUrls || imageUrls.length === 0) return;

  const uniqueUrls = Array.from(new Set(imageUrls.filter(url => !!url && url.includes(`/items/`))));
  if (uniqueUrls.length === 0) return;

  let imagePaths = uniqueUrls.map(url => {
      const parts = url.split(`/items/`);
      // Use decodeURIComponent to handle special characters in the storage path
      return parts.length > 1 ? decodeURIComponent(parts[1]) : null;
  }).filter((path): path is string => path !== null);

  if (imagePaths.length === 0) return;

  // If userId is provided, filter out URLs that are still in use in history
  if (userId) {
      try {
          const { data: historyMatches } = await supabase
            .from('shopping_history')
            .select('receipt_image_url')
            .eq('user_id', userId)
            .in('receipt_image_url', uniqueUrls);

          const { data: itemMatches } = await supabase
            .from('shopping_items')
            .select('image_url')
            .eq('user_id', userId)
            .in('image_url', uniqueUrls);

          const usedUrls = new Set([
            ...(historyMatches?.map(m => m.receipt_image_url) || []),
            ...(itemMatches?.map(m => m.image_url) || [])
          ]);

          const unusedUrls = uniqueUrls.filter(url => !usedUrls.has(url));
          
          // Get sizes for the unused ones
          let totalBytesToRemove = 0;
          const { data: allFiles } = await supabase.storage.from('items').list(userId, { limit: 1000 });
          
          imagePaths = unusedUrls.map(url => {
              const parts = url.split(`/items/`);
              const path = parts.length > 1 ? decodeURIComponent(parts[1]) : null;
              if (path) {
                const fileName = path.split('/').pop();
                const file = allFiles?.find(f => f.name === fileName);
                // @ts-ignore
                totalBytesToRemove += (file?.metadata?.size || file?.size || 0);
              }
              return path;
          }).filter((path): path is string => path !== null);

          if (imagePaths.length > 0) {
            const { error } = await supabase.storage.from('items').remove(imagePaths);
            if (!error && totalBytesToRemove > 0) {
              await supabase.rpc('decrement_storage', { p_user_id: userId, p_amount: totalBytesToRemove });
            }
          }
      } catch (usageError) {
          console.error('Error checking for image usage before deletion:', usageError);
          // Safety: if usage check fails, do NOT delete anything to avoid data loss
          return;
      }
  } else {
    try {
        const { error } = await supabase.storage.from('items').remove(imagePaths);
        if (error) {
            console.error('Error deleting multiple item images from storage:', error);
        }
    } catch (e) {
        console.error('Could not delete item images:', e);
    }
  }
};
// #endregion

// #region Categories API
export const getCategories = async (supabase: SupabaseClient, userId: string) => {
  const { data, error } = await supabase
    .from('categories')
    .select('name, color, emoji, translation_key, user_id')
    .or(`user_id.eq.${userId},user_id.eq.SYSTEM`);
  if (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }

  // Deduplicate: If user has a custom category with same name as SYSTEM, prefer user's
  const uniqueCategories = new Map<string, any>();

  // Sort so SYSTEM comes first, then User overrides it
  const sorted = (data || []).sort((a, b) => {
    if (a.user_id === 'SYSTEM' && b.user_id !== 'SYSTEM') return -1;
    if (a.user_id !== 'SYSTEM' && b.user_id === 'SYSTEM') return 1;
    return 0;
  });

  sorted.forEach(cat => {
    uniqueCategories.set(cat.name, cat);
  });

  // Mapping with automatic translationKey recovery for default categories
  const defaultMap: Record<string, string> = {
    'Fresh Produce': 'categories.fresh_produce',
    'Meat & Seafood': 'categories.meat_seafood',
    'Dairy & Eggs': 'categories.dairy_eggs',
    'Bakery & Bread': 'categories.bakery_bread',
    'Electronics': 'categories.electronics',
    'Apparel': 'categories.apparel',
    'Household': 'categories.household',
    'Health & Care': 'categories.health_care',
    'Other': 'categories.other'
  };

  return Array.from(uniqueCategories.values()).map(cat => ({
    name: cat.name,
    color: cat.color,
    emoji: cat.emoji,
    translationKey: cat.translation_key || defaultMap[cat.name],
    isSystem: cat.user_id === 'SYSTEM'
  }));
};
export const setCategories = async (supabase: SupabaseClient, userId: string, categories: CategoryInfo[]) => {
    // 1. Get current categories from DB
    const { data: dbCategories, error: dbError } = await supabase
        .from('categories')
        .select('name')
        .eq('user_id', userId);
    if (dbError) throw dbError;

    const dbCategoryNames = (dbCategories || []).map(c => c.name);
    const clientCategoryNames = categories.map(c => c.name);

    // 2. Find categories to delete
    const categoriesToDelete = dbCategoryNames.filter(name => !clientCategoryNames.includes(name));
    if (categoriesToDelete.length > 0) {
        const { error: deleteError } = await supabase
            .from('categories')
            .delete()
            .eq('user_id', userId)
            .in('name', categoriesToDelete);
        if (deleteError) throw deleteError;
    }

    // 3. Persist current categories (Only Truly CUSTOM ones)
    // Core categories are now served from the 'SYSTEM' user_id and should not be duplicated.
    const customCategoriesToUpsert = categories
        .filter(c => !c.translationKey) // Only categories WITHOUT a translation key are custom
        .map(c => ({ 
            user_id: userId,
            name: c.name,
            color: c.color,
            emoji: c.emoji,
            translation_key: null // Custom categories never have a translation key
        }));
    
    if (customCategoriesToUpsert.length === 0) {
        // If no custom categories, we are done (deletions already handled above)
        return null;
    }

    const { data, error: upsertError } = await supabase
      .from('categories')
      .upsert(customCategoriesToUpsert, { onConflict: 'user_id,name' });

    if (upsertError) {
      console.error('Error upserting categories:', upsertError);
      throw upsertError;
    }
  
    return data;
  };
// #endregion

// #region Stores API
/**
 * Stores from the shared directory (public.stores) within `radiusMiles` of a
 * point, nearest first. Bounding box in the query, exact distance here.
 */
export const getStoresNear = async (
  supabase: SupabaseClient,
  lat: number,
  lng: number,
  radiusMiles: number = 5,
  limit: number = 100
): Promise<Array<StoreInfo & { distanceMiles: number }>> => {
    const latDelta = radiusMiles / 69;
    const lngDelta = radiusMiles / (69 * Math.max(Math.cos((lat * Math.PI) / 180), 0.01));
    const { data, error } = await supabase
      .from('stores')
      .select('id, name, address, url, latitude, longitude')
      .gte('latitude', lat - latDelta)
      .lte('latitude', lat + latDelta)
      .gte('longitude', lng - lngDelta)
      .lte('longitude', lng + lngDelta)
      .limit(500);
    if (error) throw error;

    const toRad = (d: number) => (d * Math.PI) / 180;
    const miles = (aLat: number, aLng: number) => {
      const h = Math.sin(toRad(aLat - lat) / 2) ** 2
        + Math.cos(toRad(lat)) * Math.cos(toRad(aLat)) * Math.sin(toRad(aLng - lng) / 2) ** 2;
      return 2 * 3959 * Math.asin(Math.sqrt(h));
    };
    return (data || [])
      .filter((s: any) => s.name && s.latitude != null && s.longitude != null)
      .map((s: any) => ({
        id: s.id,
        name: s.name,
        address: s.address || '',
        url: s.url || null,
        coordinates: { lat: Number(s.latitude), lng: Number(s.longitude) },
        source: 'discovery' as const,
        distanceMiles: miles(Number(s.latitude), Number(s.longitude)),
      }))
      .filter((s) => s.distanceMiles <= radiusMiles)
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, limit);
};

export const getStores = async (supabase: SupabaseClient) => {
    const { data, error } = await supabase
      .from('stores')
      .select('*');
    if (error) {
      console.error('Error fetching stores:', error);
      throw error;
    }
    return data;
};

export const saveStore = async (supabase: SupabaseClient, store: StoreInfo) => {
    const { id, ...storeData } = store;
    const storeToSave = {
        ...storeData,
        latitude: store.coordinates?.lat,
        longitude: store.coordinates?.lng,
        name: (store.name || 'Unknown Store').trim()
    };

    if (id) { // Update existing store
        const { data, error } = await supabase
            .from('stores')
            .update(storeToSave)
            .eq('id', id);
        if (error) throw error;
        return data;
    } else { // Insert or return existing
        // OPTIMIZED LOOKUP: Prioritize URL (Google Maps unique ID)
        let existingStoreId = null;

        if (storeToSave.url) {
            const { data: byUrl } = await supabase
                .from('stores')
                .select('id')
                .eq('url', storeToSave.url)
                .maybeSingle();
            if (byUrl) existingStoreId = byUrl.id;
        }

        // FALLBACK: Name + Address lookup if no URL match
        if (!existingStoreId && storeToSave.address) {
            const { data: byAddress } = await supabase
                .from('stores')
                .select('id')
                .eq('name', storeToSave.name)
                .eq('address', storeToSave.address)
                .maybeSingle();
            if (byAddress) existingStoreId = byAddress.id;
        }
        
        if (existingStoreId) {
            return { id: existingStoreId };
        }

        const { data, error } = await supabase
            .from('stores')
            .insert(storeToSave)
            .select()
            .maybeSingle();
        if (error) throw error;
        return data;
    }
};

// #endregion

// #region Site Scripts (Named Code Injection Snippets, by slot_key)
//
// `slot_key: 'header' | 'footer'` are the two global, page-invariant SSR
// injection points (api/index.js's CUSTOM_HEADER_BLOCK/CUSTOM_FOOTER_BLOCK —
// good for GTM/pixels/self-positioning widgets, not tied to any specific
// spot in the React tree). Any other slot_key names a targeted, in-app
// mount point (e.g. 'newsletter_footer') rendered by
// <CodeSlot slotKey="..."> (components/common/CodeSlot.tsx) — same table,
// same caching/RLS-public-read mechanism, different consumer.
export interface SiteScript {
  id: string;
  slot_key: string;
  name: string;
  code: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export const getSiteScripts = async (supabase: SupabaseClient): Promise<SiteScript[]> => {
  const { data, error } = await supabase
    .from('site_scripts')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) {
    console.error('Error fetching site scripts:', error);
    throw error;
  }
  return data || [];
};

// Enabled-only snippets for a single slot, concatenated in insertion order —
// used by <CodeSlot> for a live client-side read (mirrors the same
// filter+concatenation getCachedSiteScripts does server-side for header/footer).
export const getSiteScriptsBySlot = async (supabase: SupabaseClient, slotKey: string): Promise<string> => {
  const { data, error } = await supabase
    .from('site_scripts')
    .select('code')
    .eq('slot_key', slotKey)
    .eq('enabled', true)
    .order('created_at', { ascending: true });
  if (error) {
    console.error('Error fetching site scripts for slot:', slotKey, error);
    return '';
  }
  return (data || []).map((row) => row.code).join('\n');
};

export const createSiteScript = async (
  supabase: SupabaseClient,
  script: { slot_key: string; name: string; code: string },
  updatedBy: string
) => {
  const { data, error } = await supabase
    .from('site_scripts')
    .insert({ ...script, updated_by: updatedBy })
    .select()
    .maybeSingle();
  if (error) {
    console.error('Error creating site script:', error);
    throw error;
  }
  return data;
};

export const updateSiteScript = async (
  supabase: SupabaseClient,
  id: string,
  updates: Partial<Pick<SiteScript, 'name' | 'code' | 'enabled'>>,
  updatedBy: string
) => {
  const { data, error } = await supabase
    .from('site_scripts')
    .update({ ...updates, updated_at: new Date().toISOString(), updated_by: updatedBy })
    .eq('id', id)
    .select()
    .maybeSingle();
  if (error) {
    console.error('Error updating site script:', error);
    throw error;
  }
  return data;
};

export const deleteSiteScript = async (supabase: SupabaseClient, id: string) => {
  const { error } = await supabase
    .from('site_scripts')
    .delete()
    .eq('id', id);
  if (error) {
    console.error('Error deleting site script:', error);
    throw error;
  }
};
// #endregion

// #region Identity Resolution API
export const resolveCanonical = async (supabase: SupabaseClient, userId: string, rawName: string): Promise<{ id: string, name: string } | null> => {
  // 1. Check direct name match (canonical)
  const { data: nameMatch } = await supabase
    .from('product_canonical')
    .select('id, name')
    .eq('user_id', userId)
    .eq('name', rawName)
    .maybeSingle();

  if (nameMatch) return nameMatch;

  // 2. Check slug match (URL-safe version of name)
  const slug = rawName.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w.-]+/g, '').replace(/--+/g, '-');
  const { data: slugMatch } = await supabase
    .from('product_canonical')
    .select('id, name')
    .eq('user_id', userId)
    .eq('slug', slug)
    .maybeSingle();

  if (slugMatch) return slugMatch;

  return null;
};

export const createCanonical = async (supabase: SupabaseClient, userId: string, name: string, category: string) => {
  const slug = name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w.-]+/g, '').replace(/--+/g, '-');
  // Insert new canonical product
  const { data, error } = await supabase
    .from('product_canonical')
    .upsert({ 
      user_id: userId, 
      name: name, 
      slug: slug,
      default_category: category 
    }, { onConflict: 'user_id,name' })
    .select()
    .maybeSingle();
  
  if (error) throw error;
  return data;
};
// #endregion

// #region Shopping History API
// Shared by getHistory and searchHistory so both return identical entries.
const HISTORY_SELECT = `
        id,
        purchase_date,
        created_at,
        total_amount,
        leakage_amount,
        savings_amount,
        budget_amount,
        store_id,
        receipt_image_url,
        is_lattice_contribution,
        is_personal_trip,
        stores ( id, name, address, latitude, longitude, foursquare_id, url ),
        shopping_items (
          id, name, quantity, unit, price, category_id, is_purchased, image_url, metadata,
          brand, product, variety, size,
          canonical_id,
          product_canonical ( name, slug, upc, gtin, plu_code, brand, product, variety, size, unit ),
          categories ( name, emoji )
        )
      `;

const mapHistoryRow = (entry: any): ShoppingHistoryEntry => ({
    id: entry.id,
    date: entry.purchase_date,
    archivedAt: entry.created_at,
    totalCost: entry.total_amount,
    leakageAmount: entry.leakage_amount || 0,
    savingsAmount: entry.savings_amount || 0,
    budgetAmount: (entry as any).budget_amount ?? null,
    receiptImageUrl: entry.receipt_image_url,
    isPersonalTrip: entry.is_personal_trip !== false,
    isLatticeContribution: !!entry.is_lattice_contribution,
    storeInfo: entry.stores ? {
        id: entry.stores.id || entry.store_id,
        name: entry.stores.name || '',
        address: entry.stores.address || '',
        coordinates: typeof entry.stores.latitude === 'number' ? { lat: entry.stores.latitude, lng: entry.stores.longitude } : undefined,
        foursquare_id: entry.stores.foursquare_id || '',
        url: entry.stores.url || '',
    } : undefined,
    items: (entry.shopping_items || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        canonicalName: item.product_canonical?.name || item.name, // HYBRID READ: Use Canonical ID match first
        canonicalId: item.product_canonical?.slug || undefined,
        upc: item.upc || item.product_canonical?.upc || null,
        gtin: item.gtin || item.product_canonical?.gtin || null,
        plu_code: item.plu_code || item.product_canonical?.plu_code || null,
        brand: item.brand || item.product_canonical?.brand || null,
        product: item.product || item.product_canonical?.product || null,
        variety: item.variety || item.product_canonical?.variety || null,
        size: item.size || item.product_canonical?.size || null,
        quantity: item.quantity,
        unit: item.unit || item.product_canonical?.unit || null,
        price: item.price,
        category: item.categories?.name || 'Uncategorized',
        is_purchased: item.is_purchased,
        imageUrl: item.image_url,
        metadata: item.metadata || null,
    })),
});

export interface ArchiveAllowance {
  limit: number;
  used: number;
  remaining: number;
  /** When the next archive frees up (only once the allowance is used up). */
  nextSlotAt: string | null;
  /** Pro, Ultra and admins: no limit. */
  unlimited: boolean;
  /** Length of the rolling window, in days. */
  windowDays: number;
}

/**
 * The free plan's archive allowance (public.free_archive_allowance): personal
 * trips archived in the rolling window against the limit. archive_trip
 * enforces the same rule, so this only drives the UI.
 */
export const getArchiveAllowance = async (supabase: SupabaseClient, userId: string): Promise<ArchiveAllowance> => {
  const { data, error } = await supabase.rpc('free_archive_allowance', { p_user_id: userId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    limit: row?.archive_limit ?? 3,
    used: row?.used ?? 0,
    remaining: row?.remaining ?? 3,
    nextSlotAt: row?.next_slot_at ?? null,
    unlimited: !!row?.unlimited,
    windowDays: row?.window_days ?? 30,
  };
};

export const getHistory = async (
  supabase: SupabaseClient, 
  userId: string, 
  limit: number = 20, 
  offset: number = 0,
  bounds?: { minLat: number; maxLat: number; minLng: number; maxLng: number }
): Promise<ShoppingHistoryEntry[]> => {
    // Only the user's own trips: receipts saved as "Not my trip" (Bulk Import)
    // were someone else's purchase and only exist for the Lattice.
    let query = supabase
      .from('shopping_history')
      .select(`${HISTORY_SELECT}`)
      .eq('user_id', userId)
      .eq('is_personal_trip', true);

    // Optimization #3: Apply bounding box filter on the stores join
    if (bounds) {
      query = query
        .gte('stores.latitude', bounds.minLat)
        .lte('stores.latitude', bounds.maxLat)
        .gte('stores.longitude', bounds.minLng)
        .lte('stores.longitude', bounds.maxLng);
    }

    query = query.order('purchase_date', { ascending: false });

    if (limit > 0) {
      query = query.range(offset, offset + limit - 1);
    }

    const { data: historyData, error: historyError } = await query;
  
    if (historyError) {
      console.error('Error fetching shopping history:', historyError);
      throw historyError;
    }

    if (!historyData) return [];

    return historyData.map(mapHistoryRow);
};

/**
 * Searches ALL of a user's trips by store (name/address) or item (name,
 * brand, product, variety), every word required, newest first. Runs in the
 * database (search_shopping_history) because the History screen only loads
 * trips 20 at a time. Each entry carries matchedItemIds so the card can show
 * the items that matched.
 */
export const searchHistory = async (
  supabase: SupabaseClient,
  userId: string,
  searchText: string,
  limit: number = 20,
  offset: number = 0
): Promise<Array<ShoppingHistoryEntry & { matchedItemIds: string[] }>> => {
    const { data: hits, error } = await supabase.rpc('search_shopping_history', {
      p_query: searchText,
      p_limit: limit,
      p_offset: offset,
    });
    if (error) throw error;
    if (!hits || hits.length === 0) return [];

    const ids = hits.map((h: any) => h.history_id);
    const { data: rows, error: rowsError } = await supabase
      .from('shopping_history')
      .select(HISTORY_SELECT)
      .eq('user_id', userId)
      .in('id', ids);
    if (rowsError) throw rowsError;

    const byId = new Map((rows || []).map((r: any) => [r.id, r]));
    // Keep the search's newest-first order.
    return hits
      .filter((h: any) => byId.has(h.history_id))
      .map((h: any) => ({ ...mapHistoryRow(byId.get(h.history_id)), matchedItemIds: h.matched_item_ids || [] }));
};

export const getHistoryEntry = async (supabase: SupabaseClient, userId: string, historyId: string): Promise<ShoppingHistoryEntry | null> => {
    const { data: entry, error } = await supabase
      .from('shopping_history')
      .select(`
        id,
        purchase_date,
        created_at,
        total_amount,
        leakage_amount,
        savings_amount,
        budget_amount,
        store_id,
        receipt_image_url,
        is_lattice_contribution,
        is_personal_trip,
        stores ( id, name, address, latitude, longitude, foursquare_id, url ),
        shopping_items (
          id, name, quantity, unit, price, category_id, is_purchased, image_url, metadata,
          brand, product, variety, size,
          canonical_id,
          product_canonical ( name, slug, upc, gtin, plu_code, brand, product, variety, size, unit ),
          categories ( name, emoji )
        )
      `)
      .eq('id', historyId)
      .eq('user_id', userId)
      .maybeSingle();
  
    if (error) {
      console.error('Error fetching shopping history entry:', error);
      throw error;
    }

    if (!entry) return null;

    return {
        id: entry.id,
        date: entry.purchase_date,
        totalCost: entry.total_amount,
        leakageAmount: entry.leakage_amount || 0,
        savingsAmount: entry.savings_amount || 0,
        budgetAmount: (entry as any).budget_amount ?? null,
        receiptImageUrl: entry.receipt_image_url,
        isPersonalTrip: (entry as any).is_personal_trip !== false,
        isLatticeContribution: !!(entry as any).is_lattice_contribution,
        storeInfo: entry.stores ? {
            id: (entry.stores as any).id || entry.store_id,
            name: (entry.stores as any).name || '',
            address: (entry.stores as any).address || '',
            coordinates: typeof (entry.stores as any).latitude === 'number' ? { lat: (entry.stores as any).latitude, lng: (entry.stores as any).longitude } : undefined,
            foursquare_id: (entry.stores as any).foursquare_id || '',
            url: (entry.stores as any).url || '',
        } : undefined,
        items: ((entry as any).shopping_items || []).map((item: any) => ({
            id: item.id,
            name: item.name,
            canonicalName: item.product_canonical?.name || item.name,
            canonicalId: item.product_canonical?.slug || undefined,
            upc: item.upc || item.product_canonical?.upc || null,
            gtin: item.gtin || item.product_canonical?.gtin || null,
            plu_code: item.plu_code || item.product_canonical?.plu_code || null,
            brand: item.brand || item.product_canonical?.brand || null,
            product: item.product || item.product_canonical?.product || null,
            variety: item.variety || item.product_canonical?.variety || null,
            size: item.size || item.product_canonical?.size || null,
            quantity: item.quantity,
            unit: item.unit || item.product_canonical?.unit || null,
            price: item.price,
            category: item.categories?.name || 'Uncategorized',
            is_purchased: item.is_purchased,
            imageUrl: item.image_url,
            metadata: item.metadata || null,
            shelfLife: item.metadata?.shelfLife || null,
            expirationDate: item.metadata?.expirationDate || null
        })),
    };
};

export const saveHistoryEntry = async (supabase: SupabaseClient, userId: string, entry: ShoppingHistoryEntry, categories: CategoryInfo[], isLatticeContribution: boolean = false, isPersonalTrip: boolean = true) => {
    // PREAMBLE: Storage folder check (kept client-side for now as it involves storage API)
    await ensureUserFolder(supabase, userId);

    const { data, error } = await supabase.rpc('archive_trip', {
        p_user_id: userId,
        p_store_name: entry.storeInfo?.name?.trim() || null,
        p_store_address: entry.storeInfo?.address || null,
        p_store_lat: entry.storeInfo?.coordinates?.lat ?? null,
        p_store_lng: entry.storeInfo?.coordinates?.lng ?? null,
        p_store_url: entry.storeInfo?.url || null,
        p_purchase_date: entry.date,
        p_total_amount: Number(entry.totalCost) || 0,
        p_leakage_amount: Number((entry as any).leakageAmount) || 0,
        p_savings_amount: Number((entry as any).savingsAmount) || 0,
        p_receipt_image_url: (entry.receiptImageUrl?.startsWith('local:') || entry.receiptImageUrl?.startsWith('chat:')) ? null : entry.receiptImageUrl || null,
        p_items: (entry.items || []).map(item => ({
            name: item.name,
            price: Number(item.price) || 0,
            quantity: Number(item.quantity) || 1,
            category: item.category || 'Other',
            imageUrl: (item.imageUrl?.startsWith('local:') || item.imageUrl?.startsWith('chat:')) ? null : item.imageUrl || null,
            canonicalId: (item as any).canonicalId || null,
            embedding: (item as any).embedding || null,
            shelfLife: (item as any).shelfLife || null,
            expirationDate: (item as any).expirationDate || null,
            brand: (item as any).brand || null,
            product: (item as any).product || null,
            variety: (item as any).variety || null,
            size: (item as any).size || null,
            unit: (item as any).unit || null,
            upc: (item as any).upc || null,
            gtin: (item as any).gtin || null,
            plu_code: (item as any).plu_code || null,
            metadata: (item as any).metadata || null
        })),
        p_categories: (categories || []).map(c => ({
            name: c.name,
            color: c.color,
            emoji: c.emoji || null
        })),
        p_is_lattice_contribution: isLatticeContribution,
        p_confidence_score: (entry as any).confidenceScore ?? 100,
        p_is_personal_trip: isPersonalTrip,
        // Only sent when a budget was set, so archiving keeps working against a
        // database without the trip-budget migration (archive_trip's 17th param).
        ...(entry.budgetAmount && entry.budgetAmount > 0 ? { p_budget_amount: entry.budgetAmount } : {}),
    });

    if (error) {
        console.error('SupabaseService: Error archiving trip via RPC:', error);
        throw error;
    }

    // ATOMIC SYNC: Update the last_history_change and geospatial metadata in profile
    // This allows the Global Lattice map to center on the user's regional hub.
    await supabase.rpc('patch_profile', {
      p_user_id: userId,
      p_updates: {
        last_history_change: new Date().toISOString(),
        metadata: {
          last_store_coordinates: entry.storeInfo?.coordinates || null,
          last_store_name: entry.storeInfo?.name || null
        }
      }
    });

    const result = data?.success ? data : (data?.[0]?.success ? data[0] : data);
    const historyId = result?.history_id;
    const reward = result?.reward || 0;
    
    void invalidateUserData(userId); // cached analytics/price history are now stale
    return { ...entry, id: historyId, reward };
};

// #region Analytics & Performance API
export const getItemPriceHistory = async (supabase: SupabaseClient, userId: string, slug: string) => {
    const { data, error } = await supabase.rpc('get_optimized_price_history', {
        p_slug: slug,
        p_user_id: userId
    });

    if (error) {
        console.error('SupabaseService: Error fetching price history:', error);
        throw error;
    }

    return (data || []).map((point: any) => ({
        date: point.bucket_date,
        price: Number(point.avg_price),
        minPrice: Number(point.min_price),
        maxPrice: Number(point.max_price),
        store: point.store_names
    }));
};

export const getSpendingAnalytics = async (supabase: SupabaseClient, userId: string) => {
    const { data, error } = await supabase.rpc('get_spending_analytics', {
        p_user_id: userId
    });

    if (error) {
        console.error('SupabaseService: Error fetching analytics:', error);
        throw error;
    }

    // PostgREST returns results as an array of objects where the key is the function name
    if (Array.isArray(data) && data.length > 0) {
        const rawAnalytics = data[0].get_spending_analytics || data[0];
        
        // Transform category_spend object to category_distribution array if needed
        // The UI (DashboardView.tsx:75) expects analytics.category_distribution
        if (rawAnalytics.category_spend && !rawAnalytics.category_distribution) {
            rawAnalytics.category_distribution = Object.entries(rawAnalytics.category_spend).map(([category, total]) => ({
                category,
                total
            }));
        }

        // Hydrate item_data with SKU/PLU identifiers and structural metadata from product_canonical if the RPC didn't include them
        // (This is a safety bridge until the SQL function is fully updated)
        if (rawAnalytics.item_data) {
            Object.entries(rawAnalytics.item_data).forEach(([key, item]: [string, any]) => {
                // If item has identifiers, ensure they are at the top level for UI access
                if (item.upc) rawAnalytics.item_data[key].upc = item.upc;
                if (item.gtin) rawAnalytics.item_data[key].gtin = item.gtin;
                if (item.plu_code) rawAnalytics.item_data[key].plu_code = item.plu_code;
                
                // HYDRATE STRUCTURAL METADATA for Restock Radar / Essentials consistency
                if (item.brand) rawAnalytics.item_data[key].brand = item.brand;
                if (item.product) rawAnalytics.item_data[key].product = item.product;
                if (item.variety) rawAnalytics.item_data[key].variety = item.variety;
                if (item.size) rawAnalytics.item_data[key].size = item.size;
                if (item.unit) rawAnalytics.item_data[key].unit = item.unit;
            });
        }
        
        return rawAnalytics;
    }

    return data;
};

export const deleteHistoryEntry = async (_supabase: SupabaseClient, userId: string, historyId: string, options?: { eraseImages?: boolean }) => {
    // The trip leaves the user's account; the server keeps it anonymized for
    // statistics and the Lattice (anonymize_trip).
    const token = await (window as any).Clerk?.session?.getToken({ template: 'supabase' });
    const res = await fetch(`/api/history/${encodeURIComponent(historyId)}${options?.eraseImages ? '?eraseImages=1' : ''}`, {
        method: 'DELETE',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error(`Could not delete this trip (${res.status})`);
    void invalidateUserData(userId);
};

// #endregion



// #region Global Lattice API
// Lattice reads go through the API server (/api/lattice/*), which caches them
// in Redis once for every user (services/latticeCache.js). The `supabase`
// argument is kept so call sites don't change.
// The Bulk Import background worker runs the same Lattice checks server-side,
// where there is no browser session to call these routes with, so it installs
// a transport that calls the routes' own lookup (services/latticeCache.js).
type LatticeTransport = (path: string, body: Record<string, unknown>) => Promise<any[]>;
let latticeTransport: LatticeTransport | null = null;
export const setLatticeTransport = (transport: LatticeTransport | null) => {
  latticeTransport = transport;
};

const postLattice = async (path: string, body: Record<string, unknown>) => {
  let prices: any[];
  if (latticeTransport) {
    prices = await latticeTransport(path, body);
  } else {
    let token: string | null = null;
    try {
      token = (await (window as any).Clerk?.session?.getToken({ template: 'supabase' })) ?? null;
    } catch {
      // Signed-out callers still get Lattice prices, just without is_own_pulse.
    }
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Lattice request failed with status ${response.status}`);
    }
    ({ prices } = await response.json());
  }
  // Confidence fields may arrive as strings or be absent — coerce so callers
  // can do arithmetic/comparisons directly.
  return (prices || []).map((row: any) => ({
    ...row,
    age_hours: row.age_hours !== null && row.age_hours !== undefined ? Number(row.age_hours) : null,
    sample_count: Number(row.sample_count) || 0,
    confidence: row.confidence !== null && row.confidence !== undefined ? Number(row.confidence) : 0,
  }));
};

export const getGlobalLatticePrices = async (
  _supabase: SupabaseClient,
  slug: string,
  lat?: number,
  lng?: number,
  radiusMiles: number = CALIBRATION_DEFAULTS.latticeRadiusMiles,
  lookbackHours: number = CALIBRATION_DEFAULTS.latticeHorizonHours,
  embedding?: number[] | null,
  name?: string,
  pluCode?: string | null,
  upc?: string | null,
  gtin?: string | null
) => {
  try {
    return await postLattice('/api/lattice/prices', {
      slug: slug || null,
      lat: lat ?? null,
      lng: lng ?? null,
      radiusMiles,
      lookbackHours,
      embedding: embedding && embedding.length > 0 ? embedding : null,
      name: name ?? null,
      pluCode: pluCode ?? null,
      upc: upc ?? null,
      gtin: gtin ?? null,
    });
  } catch (err) {
    console.error('SupabaseService: get_global_lattice_prices failed:', err);
    return [];
  }
};

export const getLatticeSeedItems = async (
  supabase: SupabaseClient,
  lat?: number,
  lng?: number,
  radiusMiles: number = CALIBRATION_DEFAULTS.latticeRadiusMiles,
  lookbackHours: number = CALIBRATION_DEFAULTS.latticeHorizonHours,
  limit: number = 12
): Promise<LatticeSeedItem[]> => {
  try {
    const { data, error } = await supabase.rpc('get_lattice_seed_items', {
      p_lat: lat !== undefined ? lat : null,
      p_lng: lng !== undefined ? lng : null,
      p_radius_miles: radiusMiles,
      p_lookback_hours: lookbackHours,
      p_limit: limit,
    });

    if (error) throw error;
    // NUMERIC columns come back from PostgREST as strings, not numbers —
    // coerce here so every consumer can safely call .toFixed()/arithmetic.
    return (data || []).map((row: any) => ({
      ...row,
      best_price: Number(row.best_price) || 0,
      sample_count: Number(row.sample_count) || 0,
      distance_miles: row.distance_miles !== null && row.distance_miles !== undefined ? Number(row.distance_miles) : null,
    }));
  } catch (err) {
    console.error('SupabaseService: get_lattice_seed_items failed:', err);
    return [];
  }
};

export const getBatchGlobalLatticePrices = async (
  _supabase: SupabaseClient,
  slugs: string[],
  lat?: number,
  lng?: number,
  radiusMiles: number = CALIBRATION_DEFAULTS.latticeRadiusMiles,
  lookbackHours: number = CALIBRATION_DEFAULTS.latticeHorizonHours
) => {
  try {
    return await postLattice('/api/lattice/batch', { slugs, lat: lat ?? null, lng: lng ?? null, radiusMiles, lookbackHours });
  } catch (error) {
    console.error('SupabaseService: Error fetching batch global lattice prices:', error);
    throw error;
  }
};
// #endregion
export const getLatticeDensity = async (
  supabase: SupabaseClient,
  lat: number,
  lng: number,
  radiusMiles: number,
  lookbackHours: number,
  userId?: string
) => {
  const { data, error } = await supabase.rpc('get_lattice_density', {
    p_lat: lat,
    p_lng: lng,
    p_radius_miles: radiusMiles,
    p_lookback_hours: lookbackHours,
    p_user_id: userId
  });

  if (error) {
    console.error('SupabaseService: Error fetching lattice density:', error);
    throw error;
  }

  return data ? data[0] : null;
};
// #endregion

export const spendCredits = async (supabase: SupabaseClient, userId: string, amount: number = 1) => {
  const { data, error } = await supabase.rpc('spend_credits', {
    p_user_id: userId,
    p_amount: amount
  });

  if (error) {
    console.error('SupabaseService: Error spending credits:', error);
    throw error;
  }
  return data;
};

export const ignoreCanonicalItem = async (supabase: SupabaseClient, userId: string, slug: string) => {
  const { error } = await supabase.rpc('ignore_canonical_item', {
    p_user_id: userId,
    p_slug: slug
  });

  if (error) {
    console.error('SupabaseService: Error ignoring item:', error);
    throw error;
  }
};

// #region Insights API
export const getMasterInsight = async (supabase: SupabaseClient, userId: string): Promise<InsightData | null> => {
    try {
      const { data, error } = await supabase
        .from('ai_insights')
        .select('data, generated_at')
        .eq('user_id', userId)
        .eq('insight_type', 'master_insight')
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
  
      if (error && error.code !== 'PGRST116') {
        console.error('SupabaseService: Error fetching master insight:', error);
        throw error;
      }
      
      if (!data) {
        console.log('[SupabaseService] No master insight found for user:', userId);
        return null;
      }
      
      const insightData = data.data as any;
      console.log('[SupabaseService] Retrieved master insight:', insightData.title);
      return { 
        ...insightData, 
        generatedAt: data.generated_at 
      };
    } catch (e) {
      console.error('[SupabaseService] getMasterInsight Exception:', e);
      return null;
    }
};

export const getBasketInsight = async (supabase: SupabaseClient, userId: string): Promise<{ data: any; generated_at: string } | null> => {
  try {
    const { data, error } = await supabase
      .from('ai_insights')
      .select('data, generated_at, insight_type')
      .eq('user_id', userId)
      .in('insight_type', ['basket_tips', 'basket_tips_context']);
    if (error) throw error;
    const tips = data?.find(row => row.insight_type === 'basket_tips');
    if (!tips) return null;
    const context = data?.find(row => row.insight_type === 'basket_tips_context');
    // Older clients still write/read a plain tips array. Their writes invalidate
    // provenance because they do not atomically update the matching context row.
    const signature = context?.generated_at === tips.generated_at ? context?.data?.profileSignature : null;
    return { data: { tips: Array.isArray(tips.data) ? tips.data : tips.data?.tips || [], profileSignature: signature }, generated_at: tips.generated_at };
  } catch (error) {
    console.error('[SupabaseService] getBasketInsight Exception:', error);
    return null;
  }
};

export const saveInsight = async (supabase: SupabaseClient, userId: string, type: 'master_insight' | 'basket_tips', insightData: any) => {
  const now = new Date().toISOString();
  console.log(`[SupabaseService] Saving insight type=${type} for user=${userId}`);
  const { data, error } = await supabase
    .from('ai_insights')
    .upsert(type === 'basket_tips' && !Array.isArray(insightData) ? [
      { user_id: userId, insight_type: type, data: insightData.tips, generated_at: now },
      { user_id: userId, insight_type: 'basket_tips_context', data: { profileSignature: insightData.profileSignature }, generated_at: now },
    ] : {
      user_id: userId,
      insight_type: type,
      data: insightData,
      generated_at: now
    }, { onConflict: 'user_id,insight_type' })
    .select();

  if (error) {
    console.error(`SupabaseService: Error saving insight (${type}):`, error);
    throw error;
  }

  // ATOMIC SYNC: Update the last_insight_at in profile if it's a master insight
  if (type === 'master_insight') {
    await supabase.from('profiles').update({ last_insight_at: now }).eq('id', userId);
  }

  console.log(`[SupabaseService] Insight (${type}) saved successfully`);
  return data ? data[0] : null;
};

export const saveMasterInsight = async (supabase: SupabaseClient, userId: string, insight: InsightData) => {
    return saveInsight(supabase, userId, 'master_insight', insight);
};

// #region Lattice Contribution API

/**
 * RESOLVE STORE CONTEXT:
 * This helper ensures that we find ALL store_ids associated with a physical location
 * by matching the unique Google Maps URL. If no URL exists, it falls back to the specific ID.
 */
const resolveStoreContext = async (supabase: SupabaseClient, storeUrl?: string, storeId?: string): Promise<string[]> => {
  // STRICT GATE: If no context is provided, return empty to prevent global leak
  if (!storeUrl && !storeId) return [];

  let ids: Set<string> = new Set();

  // 1. If we have a URL, find ALL stores sharing that exact Google URL (Global Sector)
  if (storeUrl) {
    const { data } = await supabase.from('stores').select('id').eq('url', storeUrl);
    if (data && data.length > 0) {
      data.forEach(s => ids.add(s.id));
    }
  }

  // 2. If we have a specific ID, always include it as a baseline
  if (storeId) {
    ids.add(storeId);
  }

  return Array.from(ids);
};
export const getPendingLatticeContributions = async (supabase: SupabaseClient, storeId?: string, storeUrl?: string) => {
  const ids = await resolveStoreContext(supabase, storeUrl, storeId);
  if ((storeId || storeUrl) && ids.length === 0) return [];

  const { data, error } = await supabase.rpc('get_pending_lattice_contributions_multi', {
    p_store_ids: ids.length > 0 ? ids : null
  });
  
  if (error) {
    console.error('SupabaseService: Error fetching pending lattice contributions:', error);
    throw error;
  }
  return data || [];
};

/** Decorative logo reads must never fail a review read or an acknowledged save. */
const readSavedStoreLogos = async (supabase: SupabaseClient, storeIds: string[]): Promise<Map<string, string | null>> => {
  if (!storeIds.length) return new Map();
  try {
    const { data, error } = await supabase.schema('public').from('stores')
      .select('id, logo_url').in('id', storeIds);
    if (error) throw error;
    return new Map((data || []).map(store => [store.id, store.logo_url]));
  } catch (error) {
    console.warn('[LatticeReview] Saved store logos unavailable', error);
    return new Map();
  }
};

export const getLatticeReviewQueue = async (supabase: SupabaseClient) => {
  const { data, error } = await supabase.rpc('get_lattice_review_queue', { p_store_ids: null, p_status: null });
  if (error) throw error;
  const rows = data || [];
  const storeIds = [...new Set(rows.map((row: any) => row.store_id).filter(Boolean))] as string[];
  if (!storeIds.length) return rows;
  // One batch for all trips; use saved logos without triggering paid discovery.
  const logos = await readSavedStoreLogos(supabase, storeIds);
  return rows.map((row: any) => ({ ...row, store_logo_url: logos.get(row.store_id) || null }));
};
export const setLatticeReviewStatus = async (supabase: SupabaseClient, trip: any, status: 'pending' | 'rejected') => {
  const { data, error } = await supabase.rpc('set_lattice_review_status', {
    p_history_id: trip.id, p_expected_version: trip.review_version, p_status: status,
  });
  if (error) throw error;
  void invalidateUserData(trip.user_id);
  return { ...trip, ...data };
};

/** Admin-only, optimistic-concurrency checked correction + optional approval. */
export const saveLatticeReview = async (supabase: SupabaseClient, historyId: string, version: string, trip: Record<string, unknown>, approve = false) => {
  const { data, error } = await supabase.rpc('save_lattice_review', {
    p_history_id: historyId, p_expected_version: version, p_trip: trip, p_approve: approve,
  });
  if (error) throw error;
  invalidateUserData(String(trip.user_id));
  // A correction can point at another store: return that location's own logo.
  const logos = await readSavedStoreLogos(supabase, [data.store_id]);
  return { ...data, store_logo_url: logos.get(data.store_id) || null } as { review_version: string; store_id: string; store_logo_url: string | null };
};

export const approveLatticeContribution = async (supabase: SupabaseClient, historyId: string) => {
  const { data, error } = await supabase.rpc('approve_lattice_contribution', {
    p_history_id: historyId
  });
  if (error) {
    console.error('SupabaseService: Error approving lattice contribution:', error);
    throw error;
  }
  return data;
};

export const rejectLatticeContribution = async (supabase: SupabaseClient, historyId: string) => {
  const { data, error } = await supabase.rpc('reject_lattice_contribution', {
    p_history_id: historyId
  });
  if (error) {
    console.error('SupabaseService: Error rejecting lattice contribution:', error);
    throw error;
  }
  return data;
};

export const getPublicStoreLocations = async (supabase: SupabaseClient) => {
  const { data, error } = await supabase.rpc('get_public_store_locations');
  if (error) {
    console.error('SupabaseService: Error fetching public store locations:', error);
    throw error;
  }
  return data || [];
};

export const getMerchantAnalytics = async (supabase: SupabaseClient, storeUrl?: string, storeId?: string) => {
  const ids = await resolveStoreContext(supabase, storeUrl, storeId);
  if ((storeUrl || storeId) && ids.length === 0) return [];

  // Scans plus inducted prices, summed per category in SQL (top 5).
  const { data, error } = await supabase.rpc('get_merchant_category_breakdown', {
    p_store_ids: ids.length > 0 ? ids : null
  });
  if (error) {
    console.error('SupabaseService: Error fetching merchant category breakdown:', error);
    throw error;
  }
  return (data || []).map((row: { category: string; total: number | string }) => ({ category: row.category, total: Number(row.total) }));
};

export const getMerchantPulse = async (
  supabase: SupabaseClient, 
  storeUrl?: string,
  storeId?: string,
  page: number = 0,
  pageSize: number = 5
) => {
  const ids = await resolveStoreContext(supabase, storeUrl, storeId);
  if ((storeUrl || storeId) && ids.length === 0) return [];

  // 2. Perform the Pulse Query with strict ID filtering
  const selectStr = `
    id, name, price, created_at,
    categories (name),
    shopping_history!inner (
      purchase_date,
      store_id,
      stores (name, address, url)
    )
  `;

  let query = supabase
    .from('shopping_items')
    .select(selectStr);

  // STRICT FILTERING: Match by the resolved physical store IDs
  if (ids.length > 0) {
    query = query.in('shopping_history.store_id', ids);
  } else if (storeUrl || storeId) {
    // If we have context but couldn't find IDs (safety), force fail to avoid global leak
    return [];
  }

  const from = page * pageSize;
  const to = from + pageSize - 1;

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) {
    console.error('SupabaseService: Error fetching merchant pulse:', error);
    throw error;
  }

  // SECONDARY SAFEGUARD: Ensure strictly matching ID
  const filtered = (data || []).filter((item: any) => {
    if (ids.length === 0) return true; // Global mode
    return ids.includes(item.shopping_history?.store_id);
  });

  return filtered.map((item: any) => ({
    id: item.id,
    item: item.name,
    price: `$${Number(item.price).toFixed(2)}`,
    time: new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    sentiment: Number(item.price) < 5 ? 'positive' : 'neutral', 
    store: item.shopping_history?.stores?.name,
    address: item.shopping_history?.stores?.address,
    category: item.categories?.name
  }));
};

export const getMerchantStats = async (supabase: SupabaseClient, storeUrl?: string, storeId?: string) => {
  const ids = await resolveStoreContext(supabase, storeUrl, storeId);
  if ((storeUrl || storeId) && ids.length === 0) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Today's scans
  let todayQuery = supabase
    .from('shopping_items')
    .select('id, shopping_history!inner(store_id)', { count: 'exact', head: true })
    .gte('created_at', today.toISOString());
  
  if (ids.length > 0) {
    todayQuery = todayQuery.in('shopping_history.store_id', ids);
  }
  const { count: todayScans, error: todayScanError } = await todayQuery;

  // Yesterday's scans
  let yesterdayQuery = supabase
    .from('shopping_items')
    .select('id, shopping_history!inner(store_id)', { count: 'exact', head: true })
    .gte('created_at', yesterday.toISOString())
    .lt('created_at', today.toISOString());

  if (ids.length > 0) {
    yesterdayQuery = yesterdayQuery.in('shopping_history.store_id', ids);
  }
  const { count: yesterdayScans, error: yesterdayScanError } = await yesterdayQuery;

  // Today's visitors
  let visitorQuery = supabase
    .from('shopping_history')
    .select('user_id')
    .gte('purchase_date', today.toISOString());

  if (ids.length > 0) {
    visitorQuery = visitorQuery.in('store_id', ids);
  }
  const { data: visitorData, error: visitorError } = await visitorQuery;

  // Yesterday's visitors
  let yesterdayVisitorQuery = supabase
    .from('shopping_history')
    .select('user_id')
    .gte('purchase_date', yesterday.toISOString())
    .lt('purchase_date', today.toISOString());

  if (ids.length > 0) {
    yesterdayVisitorQuery = yesterdayVisitorQuery.in('store_id', ids);
  }
  const { data: yesterdayVisitorData, error: yesterdayVisitorError } = await yesterdayVisitorQuery;

  if (todayScanError || yesterdayScanError || visitorError || yesterdayVisitorError) {
    console.error('SupabaseService: Error fetching merchant stats:', { todayScanError, yesterdayScanError, visitorError, yesterdayVisitorError });
    // Fallback if errors occur
  }

  const uniqueVisitors = new Set((visitorData || []).map((v: any) => v.user_id)).size;
  const uniqueYesterdayVisitors = new Set((yesterdayVisitorData || []).map((v: any) => v.user_id)).size;

  const scanChange = yesterdayScans && yesterdayScans > 0 
    ? ((Number(todayScans || 0) - yesterdayScans) / yesterdayScans * 100).toFixed(0)
    : '0';
  
  const visitorChange = uniqueYesterdayVisitors > 0
    ? ((uniqueVisitors - uniqueYesterdayVisitors) / uniqueYesterdayVisitors * 100).toFixed(0)
    : '0';

  // Real-ish Market Sentiment: based on recent price history volume vs average
  // For demo, we'll keep it high but slightly variable
  const sentiment = 90 + Math.floor(Math.random() * 8);

  return [
    { 
      label: "Today's Scans", 
      value: todayScans || 0, 
      change: `${Number(scanChange) >= 0 ? '+' : ''}${scanChange}%`, 
      positive: Number(scanChange) >= 0, 
      icon: 'Scan' 
    },
    { 
      label: 'Unique Visitors', 
      value: uniqueVisitors || 0, 
      change: `${Number(visitorChange) >= 0 ? '+' : ''}${visitorChange}%`, 
      positive: Number(visitorChange) >= 0, 
      icon: 'Users' 
    },
    { 
      label: 'Market Sentiment', 
      value: `${sentiment}%`, 
      change: '+2%', 
      positive: true, 
      icon: 'Activity' 
    },
  ];
};

export const getScoutStats = async (supabase: SupabaseClient, userId: string) => {
  const { data, error } = await supabase.rpc('get_scout_stats', {
    p_user_id: userId
  });
  if (error) {
    console.error('SupabaseService: Error fetching scout stats:', error);
    throw error;
  }
  return data || { total_scans: 0, verified_scans: 0, pending_scans: 0, total_rewards: 0 };
};

export const getScoutWorkLog = async (supabase: SupabaseClient, userId: string) => {
  const { data, error } = await supabase.rpc('get_scout_work_log', {
    p_user_id: userId
  });
  if (error) {
    console.error('SupabaseService: Error fetching scout work log:', error);
    throw error;
  }
  return data || [];
};
// #endregion

// #region Identity Cache API
/**
 * HEURISTIC 2.1: Cache-First Identity Lookup.
 * Checks the global product_canonical table for an existing grounded record.
 */
export const checkIdentityCache = async (
  supabase: SupabaseClient,
  brand: string,
  product: string,
  size: string,
  unit: string
) => {
  const { data, error } = await supabase.rpc('check_identity_cache', {
    p_brand: brand,
    p_product: product,
    p_size: size,
    p_unit: unit
  });

  if (error) {
    console.warn('[SupabaseService] Identity cache lookup failed:', error);
    return null;
  }
  return data;
};

/**
 * Direct lookup by UPC/GTIN.
 * Optimized for Heuristic 2.1 separate columns.
 */
export const getProductByUPC = async (supabase: SupabaseClient, upc: string) => {
  // Normalize for lookup
  const digits = upc.replace(/\D/g, '');
  const gtin14 = digits.padStart(14, '0');

  const { data, error } = await supabase
    .from('product_canonical')
    .select('upc, gtin, plu_code, slug, name, brand, product, variety, size, unit, default_category')
    .or(`gtin.eq.${gtin14},upc.eq.${digits}`)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('[SupabaseService] getProductByUPC failed:', error);
    return null;
  }
  return data;
};

/**
 * Public Discovery Logic
 * Aggregated price data for SEO pages.
 */
export const getPublicLatticeData = async (
  supabase: SupabaseClient,
  citySlug: string,
  productSlug?: string,
  radiusMiles: number = 15
) => {
  const { data, error } = await supabase.rpc('get_public_lattice_data', {
    p_city_slug: citySlug,
    p_product_slug: productSlug || null,
    p_radius_miles: radiusMiles
  });

  if (error) {
    console.error('[SupabaseService] getPublicLatticeData failed:', error);
    return [];
  }
  return data || [];
};
// #endregion

// #endregion

// #region Directory Listings

export interface DirectoryListing {
  id: string;
  name: string;
  url: string;
  logo_url: string | null;
  embed_code: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface DirectoryListingPage {
  slug: string;
  page_type: 'blog' | 'newsroom' | 'landing';
  created_at: string;
}

export const getActiveDirectoryListings = async (supabase: SupabaseClient): Promise<DirectoryListing[]> => {
  const { data, error } = await supabase
    .from('directory_listings')
    .select('*')
    .eq('is_active', true)
    .order('display_order');
  if (error) { console.error('[SupabaseService] getActiveDirectoryListings failed:', error); return []; }
  return data || [];
};

export const getAllDirectoryListings = async (supabase: SupabaseClient): Promise<DirectoryListing[]> => {
  const { data, error } = await supabase
    .from('directory_listings')
    .select('*')
    .order('display_order');
  if (error) { console.error('[SupabaseService] getAllDirectoryListings failed:', error); throw error; }
  return data || [];
};

export const createDirectoryListing = async (
  supabase: SupabaseClient,
  listing: Omit<DirectoryListing, 'id' | 'created_at' | 'updated_at'>
): Promise<DirectoryListing> => {
  const { data, error } = await supabase
    .from('directory_listings')
    .insert(listing)
    .select()
    .single();
  if (error) { console.error('[SupabaseService] createDirectoryListing failed:', error); throw error; }
  return data;
};

export const updateDirectoryListing = async (
  supabase: SupabaseClient,
  id: string,
  updates: Partial<Omit<DirectoryListing, 'id' | 'created_at'>>
): Promise<DirectoryListing> => {
  const { data, error } = await supabase
    .from('directory_listings')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) { console.error('[SupabaseService] updateDirectoryListing failed:', error); throw error; }
  return data;
};

export const deleteDirectoryListing = async (supabase: SupabaseClient, id: string): Promise<void> => {
  const { error } = await supabase.from('directory_listings').delete().eq('id', id);
  if (error) { console.error('[SupabaseService] deleteDirectoryListing failed:', error); throw error; }
};

export const getDirectoryListingPages = async (supabase: SupabaseClient): Promise<DirectoryListingPage[]> => {
  const { data, error } = await supabase
    .from('directory_listing_pages')
    .select('*')
    .order('page_type');
  if (error) { console.error('[SupabaseService] getDirectoryListingPages failed:', error); throw error; }
  return data || [];
};

export const isDirectoryListingsEnabledForSlug = async (
  supabase: SupabaseClient,
  slug: string,
  pageType: 'blog' | 'newsroom' | 'landing'
): Promise<boolean> => {
  const { data } = await supabase
    .from('directory_listing_pages')
    .select('slug')
    .eq('page_type', pageType)
    .in('slug', ['__all__', slug]);
  return Array.isArray(data) && data.length > 0;
};

export const setDirectoryListingPageEnabled = async (
  supabase: SupabaseClient,
  slug: string,
  pageType: 'blog' | 'newsroom' | 'landing',
  enabled: boolean
): Promise<void> => {
  if (enabled) {
    const { error } = await supabase
      .from('directory_listing_pages')
      .upsert({ slug, page_type: pageType });
    if (error) { console.error('[SupabaseService] setDirectoryListingPageEnabled (add) failed:', error); throw error; }
  } else {
    const { error } = await supabase
      .from('directory_listing_pages')
      .delete()
      .eq('slug', slug)
      .eq('page_type', pageType);
    if (error) { console.error('[SupabaseService] setDirectoryListingPageEnabled (remove) failed:', error); throw error; }
  }
};

// #endregion

// #region Restock signals

/**
 * The restock signals saved in item metadata (metadata.family,
 * metadata.replenishable), newest first, one per product. Read from
 * shopping_items rather than get_spending_analytics, so they don't depend on
 * that RPC's shape and cover the whole history, not just the loaded trips.
 */
export const getRestockSignals = async (supabase: SupabaseClient, userId: string): Promise<RestockSignal[]> => {
  const { data, error } = await supabase
    .from('shopping_items')
    .select('family:metadata->>family, replenishable:metadata->replenishable, product_canonical ( slug ), shopping_history!inner ( is_personal_trip )')
    .eq('user_id', userId)
    // Restock is about what the user buys, so "Not my trip" receipts don't count.
    .eq('shopping_history.is_personal_trip', true)
    .or('metadata->>family.not.is.null,metadata->>replenishable.not.is.null')
    .order('created_at', { ascending: false })
    .limit(5000);
  if (error) {
    console.error('SupabaseService: Error loading restock signals:', error);
    throw error;
  }
  const bySlug = new Map<string, RestockSignal>();
  for (const row of (data || []) as any[]) {
    const slug = row.product_canonical?.slug;
    if (!slug) continue;
    const signal: RestockSignal = bySlug.get(slug) || { slug };
    if (signal.family == null && typeof row.family === 'string' && row.family) signal.family = row.family;
    if (signal.replenishable == null && typeof row.replenishable === 'boolean') signal.replenishable = row.replenishable;
    bySlug.set(slug, signal);
  }
  return [...bySlug.values()];
};
// #endregion
